import { useState } from "react";
import { Link } from "react-router-dom";
import type { SupplierOrderBatch } from "@dentacore/shared-types";

import { checkoutCart, markOrdersSent } from "../api/suppliers";
import inv from "../inventory/inventory.module.css";
import { batchMessage } from "../inventory/supplier-text";
import styles from "../inventory/suppliers.module.css";
import { formatMoney } from "../patients/money";
import type { Writer } from "../settings/use-writer";
import { Modal } from "../ui/modal";
import { describeNumberError, NumberField } from "../ui/number-field";
import { Pager, fitArea, useFitPages } from "../ui/paging";
import { useCart } from "./cart";
import type { CartLine } from "./cart";
import c from "./cart.module.css";
import { atLocation } from "../api/client";
import { Badges, useShown } from "../shell/locations";

/** Whole packs, at least one. A bound, not a clinic's data. */
const PACKS = { min: 1, integer: true, unit: "packs" } as const;

/** 24×24 stroke glyphs, drawn the way the sidebar's are. */
const GLYPH = {
  bag: "M5 8h14l-1 12.5H6zM9 8V6.5a3 3 0 0 1 6 0V8",
  box: "M3.5 8 12 4l8.5 4v8.5L12 20.5 3.5 16.5zM3.5 8 12 12l8.5-4M12 12v8.5",
  shield: "M12 3.5 19 6v5.5c0 4.2-3 7.6-7 9-4-1.4-7-4.8-7-9V6zM9 12l2 2 4-4",
  back: "M19 12H5M11 6l-6 6 6 6",
};

function Glyph({ d, className }: { d: string; className?: string }): React.JSX.Element {
  return (
    <svg className={className ?? c.glyph} viewBox="0 0 24 24" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

/**
 * **The cart, and checking it out** (STORY-189c, MOD-D23, MOD-D24).
 *
 * **Drawn as a shop's cart page** (Emile, 2026-10-16, pointing at a shopping-cart
 * design: *"still ugly, try something like this"*): each item as its own tile
 * with a thumbnail, its supplier and pack, its price, a − / + for packs, its
 * total and ×; *Continue shopping* back to the catalogue; and an order summary
 * card beside it with each supplier's subtotal, the total and one button. The
 * tiles page to fit rather than scrolling (PF4).
 *
 * A checkout makes one order per supplier, each with a single message to copy.
 * **DentaCore never sends it** (SU-D4) — the assistant does, and then Order
 * history is where it is received. A dearer supplier asks for a reason, as a
 * single order does.
 */
export function CartTab({
  currency,
  canOrder,
  busy,
  run,
  onPlaced,
}: {
  currency: string;
  canOrder: boolean;
  busy: boolean;
  run: Writer["run"];
  /** Called when the checkout landed and the result dialog is closed. */
  onPlaced: () => void;
}): React.JSX.Element {
  const cart = useCart();
  const places = useShown();
  const [placed, setPlaced] = useState<readonly SupplierOrderBatch[] | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  /** Batches marked sent from this dialog (SU-D18). */
  const [sent, setSent] = useState<ReadonlySet<string>>(new Set());

  // Grouped by supplier, in the order the suppliers were first added.
  const order = [...new Set(cart.lines.map((line) => line.supplierId))];
  const lines = [...cart.lines].sort(
    (a, b) => order.indexOf(a.supplierId) - order.indexOf(b.supplierId),
  );
  const suppliers = order.map((id) => {
    const theirs = lines.filter((line) => line.supplierId === id);
    return { id, name: theirs[0]?.supplierName ?? "", lines: theirs };
  });

  const packsOf = (packs: string): number => (/^\d+$/.test(packs) ? Number(packs) : 0);
  const lineTotal = (l: CartLine): number => l.priceCents * packsOf(l.packs);
  const grand = lines.reduce((sum, l) => sum + lineTotal(l), 0);
  const packCount = lines.reduce((sum, l) => sum + packsOf(l.packs), 0);
  const cheapestCount = lines.filter((l) => l.cheapest).length;
  const badPacks = lines.some((l) => describeNumberError(l.packs, PACKS) !== null);
  const noReason = lines.some((l) => !l.cheapest && l.reason.trim() === "");

  const paging = useFitPages({
    keys: lines.map((line) => line.inventoryItemId),
    estimate: (index) => (lines[index]?.cheapest === false ? 128 : 90),
    gap: 10,
  });
  const [from, to] = paging.range;
  const shown = lines.slice(from, to);

  const copy = (key: string, text: string): void => {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
    });
  };

  const step = (line: CartLine, by: number): void => {
    cart.update(line.inventoryItemId, { packs: String(Math.max(1, packsOf(line.packs) + by)) });
  };

  return (
    <div className={c.page} data-testid="cart-tab">
      {/* Empty — also behind the dialog once a checkout has emptied it. */}
      {lines.length === 0 ? (
        <section className={c.emptyCard} data-testid="cart-empty">
          <span className={c.emptyBadge}>
            <Glyph d={GLYPH.bag} className={c.emptyGlyph} />
          </span>
          <h2 className={c.emptyTitle}>Your cart is empty</h2>
          <p className={c.emptyText}>
            Add items from the catalog — each goes to the supplier you pick, and checking out makes
            one order per supplier.
          </p>
          <Link to="/supply/catalogue" className={inv.primary}>
            Browse the catalog
          </Link>
        </section>
      ) : (
        <div className={c.layout}>
          <section className={c.cartCard}>
            <div className={c.cartHead}>
              <h2 className={c.cartTitle}>
                <Glyph d={GLYPH.bag} />
                Shopping cart
              </h2>
              <span className={c.cartCount}>
                {lines.length} {lines.length === 1 ? "item" : "items"}
              </span>
            </div>

            <div className={c.columns} aria-hidden="true">
              <span>Item details</span>
              <span className={c.center}>Price</span>
              <span className={c.center}>Packs</span>
              <span className={c.right}>Total</span>
            </div>

            <div className={fitArea} ref={paging.areaRef}>
              <ul className={c.tiles}>
                {shown.map((line) => (
                  <li
                    key={line.inventoryItemId}
                    ref={paging.measure(line.inventoryItemId)}
                    className={c.tile}
                    data-testid={`cart-line-${line.inventoryItemId}`}
                  >
                    <div className={c.tileRow}>
                      <span className={c.item}>
                        <span className={c.thumb}>
                          <Glyph d={GLYPH.box} className={c.thumbGlyph} />
                        </span>
                        <span className={c.itemText}>
                          <span className={c.name}>
                            {line.itemName}{" "}
                            <Badges ats={line.at === undefined ? [] : [line.at]} shown={places} />
                          </span>
                          <span className={c.attr}>
                            <span>Supplier</span>
                            {line.supplierName}
                          </span>
                          <span className={c.attr}>
                            <span>Pack</span>
                            {line.pack}
                          </span>
                        </span>
                      </span>

                      <span className={c.price}>
                        <b className={line.cheapest ? c.priceBest : undefined}>
                          {formatMoney(line.priceCents, currency)}
                        </b>
                        <span className={line.cheapest ? c.best : c.dearer}>
                          {line.cheapest ? "cheapest" : "not the cheapest"}
                        </span>
                      </span>

                      <span className={c.center}>
                        <span className={c.stepper}>
                          <button
                            type="button"
                            className={c.stepButton}
                            disabled={packsOf(line.packs) <= 1}
                            onClick={() => step(line, -1)}
                            aria-label={`One pack fewer of ${line.itemName}`}
                          >
                            −
                          </button>
                          <NumberField
                            label={`Packs of ${line.itemName}`}
                            value={line.packs}
                            rules={PACKS}
                            testId={`cart-packs-${line.inventoryItemId}`}
                            className={c.qty}
                            labelClassName={c.hidden}
                            inputClassName={c.qtyInput}
                            onChange={(next) => {
                              cart.update(line.inventoryItemId, { packs: next });
                            }}
                          />
                          <button
                            type="button"
                            className={c.stepButton}
                            onClick={() => step(line, 1)}
                            aria-label={`One pack more of ${line.itemName}`}
                          >
                            +
                          </button>
                        </span>
                      </span>

                      <span className={c.lineTotal}>{formatMoney(lineTotal(line), currency)}</span>

                      <button
                        type="button"
                        className={`${inv.close} ${c.remove}`}
                        aria-label={`Remove ${line.itemName} from the cart`}
                        data-testid={`cart-remove-${line.inventoryItemId}`}
                        onClick={() => {
                          cart.remove(line.inventoryItemId);
                        }}
                      >
                        ×
                      </button>
                    </div>

                    {!line.cheapest && (
                      <label className={c.reason}>
                        <span>Why this supplier?</span>
                        <input
                          value={line.reason}
                          placeholder="Faster delivery, the brand we use…"
                          aria-label={`Why ${line.itemName} is not from the cheapest supplier`}
                          data-testid={`cart-reason-${line.inventoryItemId}`}
                          onChange={(event) => {
                            cart.update(line.inventoryItemId, { reason: event.target.value });
                          }}
                        />
                      </label>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className={c.cartFoot}>
              <Link to="/supply/catalogue" className={c.continue}>
                <Glyph d={GLYPH.back} />
                Continue shopping
              </Link>
              <button
                type="button"
                className={inv.quiet}
                onClick={cart.clear}
                data-testid="cart-clear"
              >
                Empty the cart
              </button>
              <span className={c.spacer} />
              <Pager
                compact
                page={paging.page}
                count={paging.pages.length}
                onPage={paging.setPage}
                from={from + 1}
                to={to}
                total={lines.length}
                noun="items"
                testId="cart-pager"
              />
            </div>
          </section>

          <aside className={c.summary} data-testid="cart-summary">
            <h2 className={c.summaryTitle}>Order summary</h2>

            <div className={c.summaryBody}>
              <p className={c.detailsHead}>
                Price details ({packCount} {packCount === 1 ? "pack" : "packs"})
              </p>
              <ul className={c.details}>
                {suppliers.map((s) => (
                  <li key={s.id}>
                    <span>{s.name}</span>
                    <span className={c.num}>
                      {formatMoney(
                        s.lines.reduce((sum, l) => sum + lineTotal(l), 0),
                        currency,
                      )}
                    </span>
                  </li>
                ))}
                <li>
                  <span>Orders to place</span>
                  <span className={c.num}>{suppliers.length}</span>
                </li>
              </ul>

              <div className={c.total} data-testid="cart-total">
                <span>Total amount</span>
                <b>{formatMoney(grand, currency)}</b>
              </div>

              {canOrder ? (
                <button
                  type="button"
                  className={c.placeButton}
                  disabled={busy || badPacks || noReason}
                  data-testid="cart-checkout"
                  onClick={() => {
                    void run(
                      async () => {
                        // Each location orders its own lines: one checkout per location.
                        const ats = [...new Set(lines.map((l) => l.at ?? null))];
                        const batches = await Promise.all(
                          ats.map((at) =>
                            atLocation(at, () =>
                              checkoutCart(
                                lines
                                  .filter((l) => (l.at ?? null) === at)
                                  .map((l) => ({
                                    inventoryItemId: l.inventoryItemId,
                                    supplierPriceId: l.supplierPriceId,
                                    packs: packsOf(l.packs),
                                    ...(l.cheapest ? {} : { reason: l.reason.trim() }),
                                  })),
                              ),
                            ),
                          ),
                        );
                        setPlaced(batches.flat());
                      },
                      // The server took the ordered lines out of the shared cart; a
                      // colleague's line added meanwhile stays (SU-D17).
                      async () => {
                        cart.reload();
                      },
                      "Orders placed.",
                    );
                  }}
                >
                  Place {suppliers.length} {suppliers.length === 1 ? "order" : "orders"}
                </button>
              ) : (
                <p className={c.warn}>Ordering is for whoever records stock movements.</p>
              )}

              {noReason ? (
                <p className={c.warn}>Say why for each item not from the cheapest supplier.</p>
              ) : badPacks ? (
                <p className={c.warn}>Every item needs at least one pack.</p>
              ) : (
                <p className={c.good}>
                  {cheapestCount === lines.length
                    ? "Every item is from its cheapest supplier."
                    : `${String(cheapestCount)} of ${String(lines.length)} items from their cheapest supplier.`}
                </p>
              )}
            </div>

            <p className={c.trust}>
              <Glyph d={GLYPH.shield} />
              DentaCore never sends an order. You copy one message to each supplier, and receive
              what arrives under Order history.
            </p>
          </aside>
        </div>
      )}

      {placed !== null && (
        <Modal
          label="Orders placed"
          onClose={() => {
            setPlaced(null);
            onPlaced();
          }}
        >
          <div className={styles.widePanel} data-testid="checkout-result">
            <h2>Orders placed</h2>
            <p className={styles.meta}>
              DentaCore does not send them. Copy each message to its supplier and mark it sent; an
              order not marked sent says so under Order history, where its message stays.
            </p>
            {placed.map((batch) => {
              const message = batchMessage(
                batch.supplierName,
                batch.orders.map((o) => ({
                  packs: o.packs,
                  itemName: o.itemName,
                  pack: o.pack,
                  priceCents: o.priceCents,
                })),
                batch.totalCents,
                currency,
                formatMoney,
              );
              return (
                <div key={batch.id} data-testid={`checkout-batch-${batch.supplierId}`}>
                  <h3>
                    {batch.supplierName}
                    {batch.supplierPhone !== null && ` · ${batch.supplierPhone}`}
                  </h3>
                  <p className={styles.message}>{message}</p>
                  <button
                    type="button"
                    className={inv.quiet}
                    onClick={() => {
                      copy(batch.id, message);
                    }}
                  >
                    {copied === batch.id ? "Copied" : "Copy the message"}
                  </button>{" "}
                  {/* Once it has gone to the supplier (SU-D18); Order history asks again if not. */}
                  <button
                    type="button"
                    className={inv.ghost}
                    disabled={busy || sent.has(batch.id)}
                    onClick={() => {
                      void run(
                        () => markOrdersSent(batch.orders.map((order) => order.id)),
                        async () => {
                          setSent((was) => new Set([...was, batch.id]));
                        },
                        `Marked sent to ${batch.supplierName}.`,
                      );
                    }}
                    data-testid={`checkout-sent-${batch.supplierId}`}
                  >
                    {sent.has(batch.id) ? "Sent" : "Mark as sent"}
                  </button>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
    </div>
  );
}
